<script setup lang="ts">
import { login, logout } from '~/composables/useSyncAuth'
import { BREAKPOINT_MD } from '~/constants/page-constants'
import { onClickOutside, useMediaQuery } from '@vueuse/core'

const props = defineProps<{
  shouldCloseMobileMenu: boolean
}>()

const isLoggedIn = useLoggedInState()

const menuRef = ref(null)
const isMenuOpened = ref(false)

// Ensure menu closes when user clicks outside of the menu itself
onClickOutside(
  menuRef,
  () => {
    isMenuOpened.value = false
  },
)

// Close menu when scrolling down the page on mobile
const { shouldCloseMobileMenu } = toRefs(props)
watch(shouldCloseMobileMenu, (newValue) => {
  if (newValue && isSmallerThanMediumViewport.value) {
    isMenuOpened.value = false
  }
})

const isSmallerThanMediumViewport = computed((): boolean => {
  return useMediaQuery(`(max-width: ${BREAKPOINT_MD - 1}px)`).value
})

const { accountBaseUrl, panelBaseUrl } = useAppConfig()
const route = useRoute()

const logoutUser = (): void => {
  logout()
  isMenuOpened.value = false
}
</script>

<template>
  <div
    ref="menuRef"
    class="app-user-profile-menu"
  >
    <UiButton
      class="app-user-profile-menu__button"
      @click="isMenuOpened = !isMenuOpened"
    >
      <span class="sr-only">{{ isLoggedIn ? 'User is logged in' : 'User is not logged in' }}</span>
      <Icon
        class="app-user-profile-menu__button-icon"
        aria-hidden="true"
        name="fa6-solid:user"
        size="20px"
      />
      <Icon
        v-if="isLoggedIn"
        class="app-user-profile-menu__button-icon app-user-profile-menu__logout-button-icon"
        aria-hidden="true"
        name="fa6-solid:check"
        size="10px"
      />
      <Icon
        v-else
        class="app-user-profile-menu__button-icon app-user-profile-menu__login-button-icon"
        aria-hidden="true"
        name="fa6-solid:x"
        size="8px"
      />
    </UiButton>
    <div
      class="app-user-profile-menu__menu list-reset"
      :class="{ '--opened': isMenuOpened }"
    >
      <template v-if="isLoggedIn">
        <NuxtLink
          class="app-user-profile-menu__menu-link"
          target="_blank"
          :to="`${panelBaseUrl}/v2/settings/account`"
        >
          <Icon
            aria-hidden="true"
            name="fa6-solid:user"
          />
          Account
        </NuxtLink>
        <NuxtLink
          class="app-user-profile-menu__menu-link"
          target="_blank"
          :to="`${panelBaseUrl}/v2/settings/integrations/manage`"
        >
          <Icon
            aria-hidden="true"
            name="fa6-solid:plug"
          />
          Connected Apps
        </NuxtLink>
        <UiButton
          class="app-user-profile-menu__menu-link"
          @click="logoutUser"
        >
          <Icon
            aria-hidden="true"
            name="fa6-solid:power-off"
          />
          Log out
        </UiButton>
      </template>
      <template v-else>
        <UiButton
          type="secondary"
          class="app-user-profile-menu__menu-button"
          @click="login"
        >
          Log in
        </UiButton>
        <UiAnchor
          type="primary"
          class="app-user-profile-menu__menu-button"
          :to="`${accountBaseUrl}/signup/?next=${encodeURIComponent(route.fullPath)}`"
        >
          Sign up
        </UiAnchor>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.app-user-profile-menu {
  position: relative;
}

.app-user-profile-menu__menu {
  display: none;

  &.--opened {
    display: flex;
    flex-direction: column;
    position: absolute;
    z-index: 1;
    padding: var(--app-spacer-16) 0;
    right: 0;
    top: var(--app-spacer-64);
    border-radius: var(--app-border-radius-small);
    background-color: var(--app-color-panel-background);
    box-shadow: var(--app-panel-shadow);
  }
}

.app-user-profile-menu__menu-button {
  margin: var(--app-spacer-12) var(--app-spacer-30);
  text-wrap: nowrap;
}

.app-user-profile-menu__menu-link {
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--app-spacer-12);
  padding: var(--app-spacer-8) var(--app-spacer-16);
  color: var(--app-color-dark-blue);
  text-wrap: nowrap;

  &.--active {
    color: var(--app-color-link-blue);
  }

  &:hover {
    color: var(--app-color-link-hover);
  }
}

.app-user-profile-menu__button {
  padding: var(--app-spacer-12);
}

.app-user-profile-menu__login-button-icon {
  color: var(--app-color-dark-grey);
}

.app-user-profile-menu__logout-button-icon {
  color: var(--app-color-logged-in-green);
}
</style>
